<template>
  <div class="w-full bg-gray-100">
    <button
      class="my-3 flex h-10 w-full items-center px-3"
      :title="contextBackText"
      @click="handleClickBack"
    >
      <IconCaret direction="left" class="mr-2 h-6 w-6 basis-6 text-blue-400" />
      <span class="line-clamp-1 text-base font-bold text-blue-400">
        {{ contextBackText }}
      </span>
    </button>
    <div class="container-wrap">
      <strong class="block py-2 text-sm font-bold text-gray-600">
        {{ menuType }}
      </strong>
    </div>
    <nav class="container-wrap">
      <ul v-if="menuType === EnumMenuType.REGIONS">
        <li v-for="item in groupingRegionsDisplay" :key="hash(item)">
          <button
            v-if="!item.regionGroupCode"
            class="locale-button"
            :class="
              currentRegion === (item as TRegionDisplay).regionCode && 'current'
            "
            :title="item.displayName"
            @click="handleClickRegion((item as TRegionDisplay).regionCode)"
          >
            <span class="block w-full break-words">
              {{ item.displayName }}
            </span>
            <IconCaret direction="right" class="locale-button-arrow" />
          </button>
          <div v-else>
            <p class="block w-full rounded p-2 text-left text-base font-bold">
              {{ item.displayName }}
            </p>
            <ul class="pl-4">
              <li
                v-for="subItem in (item as TRegionMenuDisplay).regions"
                :key="subItem.regionCode"
              >
                <button
                  class="locale-button"
                  :class="currentRegion === subItem.regionCode && 'current'"
                  :title="subItem.displayName"
                  @click="handleClickRegion(subItem.regionCode)"
                >
                  <span class="block w-full break-words">
                    {{ subItem.displayName }}
                  </span>
                  <IconCaret direction="right" class="locale-button-arrow" />
                </button>
              </li>
            </ul>
          </div>
        </li>
      </ul>
      <ul v-if="menuType === EnumMenuType.LANGUAGES">
        <li v-for="item in contextWebsitesDisplay" :key="item.code">
          <a
            class="locale-button"
            :href="provideLocalePath(item.code)"
            :class="locale === item.code && 'current'"
            :title="item.displayName"
          >
            <span class="block w-full break-words">
              {{ item.displayName }}
            </span>
            <IconCheck
              v-if="locale === item.code"
              direction="right"
              class="locale-button-arrow"
            />
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
  import hash from 'object-hash';

  import type { IRegion, IWebsite } from '@/assets/types/api/Common';
  import type { IRegionMenu } from '@/composables/useLanguage';

  type TRegionDisplay = IRegion & { displayName: string };
  type TRegionMenuDisplay = Omit<IRegionMenu, 'regions'> & {
    displayName: string;
    regions: TRegionDisplay[];
  };

  enum EnumMenuType {
    REGIONS = 'Regions',
    LANGUAGES = 'Languages',
  }

  const { t, locale } = useI18n();
  const menuStore = useMenuStore();
  const { currentMobileMenu } = storeToRefs(menuStore);
  const {
    provideLocalePath,
    provideLocaleName,
    provideRegionName,
    provideRegionGroupName,
    currentRegion,
    contextRegions,
    groupingRegions,
  } = useLanguage();

  const menuType = ref<EnumMenuType>(EnumMenuType.REGIONS);
  const selectedRegion = ref<string>('');

  const contextWebsites = computed((): IWebsite[] => {
    const regionItem = contextRegions.value.find(
      ({ regionCode }) => regionCode === selectedRegion.value
    );
    if (regionItem) {
      return regionItem.websites;
    }
    return [];
  });

  const groupingRegionsDisplay = computed(
    (): (TRegionDisplay | TRegionMenuDisplay)[] =>
      groupingRegions.value.map((item) => {
        if (!item.regionGroupCode) {
          const region = item as IRegion;
          return {
            ...region,
            displayName: provideRegionName(region.regionCode),
          };
        }
        const menu = item as IRegionMenu;
        return {
          ...menu,
          displayName: provideRegionGroupName(menu.regionGroupCode),
          regions: menu.regions.map((subItem) => ({
            ...subItem,
            displayName: provideRegionName(subItem.regionCode),
          })),
        };
      })
  );

  const contextWebsitesDisplay = computed(
    (): (IWebsite & { displayName: string })[] =>
      contextWebsites.value.map((item) => ({
        ...item,
        displayName: provideLocaleName(item.code),
      }))
  );

  const contextBackText = computed((): string => {
    if (menuType.value === EnumMenuType.REGIONS) {
      return t('region.change-region');
    } else {
      return provideRegionName(selectedRegion.value);
    }
  });

  const handleClickBack = (): void => {
    if (menuType.value === EnumMenuType.REGIONS) {
      currentMobileMenu.value = null;
    } else if (menuType.value === EnumMenuType.LANGUAGES) {
      menuType.value = EnumMenuType.REGIONS;
    }
  };

  const handleClickRegion = (code: string): void => {
    menuType.value = EnumMenuType.LANGUAGES;
    selectedRegion.value = code;
  };
</script>

<style lang="scss" scoped>
  .locale-button {
    @apply relative block w-full rounded py-2 pl-2 pr-7 text-left text-base font-bold;

    .locale-button-arrow {
      @apply absolute right-1 top-2 block h-6 w-6 text-gray-400;
    }

    &.current {
      @apply bg-blue-100 text-blue-400;

      .locale-button-arrow {
        @apply text-blue-400;
      }
    }
  }
</style>
